import type { Surface } from '@funkit/connect-core'

import type { SurfaceAdapter, SurfaceContext } from './adapter'
import { CoinbaseCheckoutSurface } from './CoinbaseCheckoutSurface'
import { HostedControlFrame } from './HostedControlFrame'

/** The one link type this draws; anything else is a page, not a sheet. */
const APPLE_PAY_BUTTON = 'PAYMENT_LINK_TYPE_APPLE_PAY_BUTTON'

interface CheckoutParams {
  /** Parsed at match time: a surface this claims must not throw at render. */
  checkoutUrl: URL
  /** Log context only — the contract says URL surfaces may carry none. */
  environment: string
}

function matchParams(surface: Surface): CheckoutParams | undefined {
  if (surface.kind !== 'URL_REDIRECT' || surface.provider !== 'COINBASE') {
    return undefined
  }
  // The contract requires `params`, but no response is validated before it
  // reaches here, so an absent one must fail the match rather than throw.
  const params: unknown = surface.params
  if (params === null || typeof params !== 'object') {
    return undefined
  }
  const { checkoutUrl, environment, paymentLinkType } = params as Record<
    string,
    unknown
  >
  // The URL is loaded in a WebView with Apple Pay turned on, so a scheme check
  // is what keeps a response from pointing that at something else.
  const url = httpsUrl(checkoutUrl)
  if (url === undefined) {
    return undefined
  }
  // Declining an unknown type lands on the unsupported notice, which names the
  // provider — better than a sheet that never opens.
  if (paymentLinkType !== APPLE_PAY_BUTTON) {
    return undefined
  }
  return {
    checkoutUrl: url,
    environment: typeof environment === 'string' ? environment : 'unstated',
  }
}

/** Declined rather than thrown on: a malformed value fails the match, not the screen. */
function httpsUrl(value: unknown): URL | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  try {
    const url = new URL(value)
    return url.protocol === 'https:' ? url : undefined
  } catch {
    return undefined
  }
}

/**
 * Coinbase's order arrives already made, with a hosted page that is nothing
 * but its Apple Pay button. Embedded, then: the button takes the action's
 * slot on the amount screen, as Transak's does, rather than a screen of its
 * own with a page's worth of empty space around one control.
 */
export const coinbaseApplePay: SurfaceAdapter = (surface) => {
  const params = matchParams(surface)
  if (params === undefined) {
    return undefined
  }

  return { control: 'EMBEDDED', render: renderer(params) }
}

const renderer =
  (params: CheckoutParams) =>
  ({ placement, onReady, onFailed, onSettled, report }: SurfaceContext) => (
    // White: the hosted control’s own fill, so the corners it rounds off match.
    <HostedControlFrame fill="#FFFFFF" placement={placement}>
      <CoinbaseCheckoutSurface
        checkoutUrl={buttonUrl(params)}
        environment={params.environment}
        onReady={onReady}
        onPaid={() => report({})}
        onSucceeded={() => onSettled?.()}
        onFailed={onFailed}
      />
    </HostedControlFrame>
  )

// Coinbase's dark theme renders a white Apple Pay button from the first paint.
function buttonUrl({ checkoutUrl, environment }: CheckoutParams): string {
  // A copy: the params are read on every render, so the match's URL stays as matched.
  const url = new URL(checkoutUrl.href)
  url.searchParams.set('theme', 'dark')
  if (environment === 'sandbox' && !url.searchParams.has(SANDBOX_PARAM)) {
    url.searchParams.set(SANDBOX_PARAM, 'true')
  }
  return url.toString()
}

const SANDBOX_PARAM = 'useApplePaySandbox'
